export class BaseError extends Error {
  /**
   * @param message Message of exception
   * @param innerError Error that is cause of current exception
   */
  constructor(message?: string, public readonly innerError?: unknown) {
    super(message);
  }
}

export class EvaluationError extends BaseError {
  constructor(message: string, innerError?: unknown) {
    super(message, innerError);
  }
}

export class InputRequiredError extends BaseError {
  constructor(input: string) {
    super(`"${input}" is a required input but was not provided`);
  }
}

export class InvalidAdjustmentOperationError extends BaseError {
  constructor() {
    super('Invalid adjustment operation');
  }
}

export class InvalidDateSourceError extends BaseError {
  constructor() {
    super('Invalid date source');
  }
}

export class InvalidSortDirectionError extends BaseError {
  constructor() {
    super('Invalid sort direction');
  }
}

export class InvalidHashAlgorithmError extends BaseError {
  constructor() {
    super('Invalid hash algorithm');
  }
}

export class InvalidReducerOperation extends BaseError {
  constructor() {
    super('Invalid reducer operation');
  }
}

/**
 * Throws an `InputRequiredError` if the input is null or undefined
 * @param input The input to validate is not null or undefined.
 * @param inputName The name of the input property/variable.
 */
export function requireInput(input: unknown, inputName: string): void {
  if (input === null || input === undefined) {
    throw new InputRequiredError(inputName);
  }
}

interface ErrorMessageOptions {
  /**
   * Default message to use if one is not found
   */
  defaultMessage?: string;
  /**
   * Whether to exclude error's name prefixed to message
   */
  excludeErrorName?: boolean;
}

/**
 * Get message from unknown error
 * @param err Unknown error
 * @param opts Additional format options
 * @internal
 */
export function getErrorMessage(err: unknown, opts?: ErrorMessageOptions): string {
  const defaultMessage = opts?.defaultMessage ?? 'Unexpected error occurred';

  if (typeof err === 'string' || err instanceof String) {
    return err.toString() || defaultMessage;
  }

  if (err instanceof Error) {
    let message = err.message;
    if (!opts?.excludeErrorName && err.name) {
      message = `${err.name}${message ? ': ' : ''}${message}`;
    }
    return message || defaultMessage;
  }

  if (typeof err === 'object' && err !== null && 'message' in err) {
    const message = (err as { message: unknown }).message;
    if (typeof message === 'string' || message instanceof String) {
      return message.toString() || defaultMessage;
    }
  }

  return defaultMessage;
}
